/**
 * DetailerQualifications — list page for AISC §1.7.0 §3.1 records.
 *
 * "Management selects Detailers/Checkers, and the PM keeps a record of their
 *  qualifications on file."
 *
 * URL: /detailer-qualifications
 *
 * Lists qualifications with filters by status, qualification type, expiring-soon.
 * Click a row → opens edit form. "+ New" button → opens create form.
 *
 * Backed by sfm-web-backend GET /api/detailer-qualification.
 * Defensive: renders a clear "backend not yet live" state if the endpoint is absent.
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Box, Paper, Chip,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Alert, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { HomeRounded, AddRounded, WarningAmberRounded } from "@mui/icons-material";

import {
  listDetailerQualifications,
  isQualificationCurrentlyValid,
  expirationDescription,
} from "../../utils/detailerQualificationsApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "EXPIRED", label: "Expired" },
  { value: "REVOKED", label: "Revoked" },
  { value: "PENDING_RENEWAL", label: "Pending renewal" },
];

const STATUS_COLOR = {
  ACTIVE: { bg: "#d1e7dd", color: "#0a3622" },
  EXPIRED: { bg: "#f8d7da", color: "#58151c" },
  REVOKED: { bg: "#e9ecef", color: "#495057" },
  PENDING_RENEWAL: { bg: "#fff3cd", color: "#664d03" },
};

export default function DetailerQualifications() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [expiringFilter, setExpiringFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotAvailable(false);

    const filters = {};
    if (statusFilter && statusFilter !== "ALL") filters.status = statusFilter;
    if (expiringFilter) filters.expiring_within_days = expiringFilter;

    listDetailerQualifications(filters)
      .then((data) => {
        if (cancelled) return;
        setRows((data && data.rows) || []);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 501) {
          setNotAvailable(true);
        } else {
          setError(err?.response?.data?.error || err?.message || "Failed to load qualifications.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [statusFilter, expiringFilter]);

  return (
    <>
      <Helmet><title>Detailer Qualifications | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HomeRounded fontSize="small" /> <span>Home</span>
            </Stack>
          </MuiLink>
          <Typography color="text.primary">Detailer Qualifications</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>Detailer Qualifications</Typography>
            <Typography variant="body2" sx={{ color: "#6c757d", mt: 0.5 }}>
              Per AISC §1.7.0 §3.1: "Management selects Detailers/Checkers, and the PM keeps a record of their qualifications on file."
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => navigate("/detailer-qualifications/new")}
          >
            New qualification
          </Button>
        </Stack>
        <PageGuide
          why="Show that the people detailing and checking your drawings are qualified — important when detailing is outsourced."
          who="The QM, verifying detailers and checkers — in-house or outsourced."
          what="A record of each detailer's and checker's qualifications on file."
          how="Add a person and record their qualifications; keep the record current."
          when="Before assigning detailing or checking work — especially to an outside firm."
          standard="AISC 207-25 §1.7.0 §3.1"
        />

        <Paper sx={{ p: 2, mb: 2, mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              select label="Status" size="small" sx={{ minWidth: 180 }}
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_FILTER_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select label="Expiring within" size="small" sx={{ minWidth: 180 }}
              value={expiringFilter} onChange={(e) => setExpiringFilter(e.target.value)}
            >
              <MenuItem value="">Any time</MenuItem>
              <MenuItem value="30">30 days</MenuItem>
              <MenuItem value="60">60 days</MenuItem>
              <MenuItem value="90">90 days</MenuItem>
              <MenuItem value="180">180 days</MenuItem>
            </TextField>
          </Stack>
        </Paper>

        {notAvailable && (
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>
            <strong>Backend endpoint not yet live.</strong> The detailer-qualification API
            (<code>/api/detailer-qualification</code>) returned 404. This typically means the
            sfm-web-backend AISC §1.7.0 Phase 1 deploy has not landed yet. See
            <code>outputs/a3sfm-build/aisc-detailing-1.7.0-design/02-design-and-data-model.md</code>.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <SmartTable tableId="detailer-qualifications" lockedColumns={["Detailer", "Qualification", "Expiration", "Status"]}>
            <TableHead>
              <TableRow>
                <TableCell>Detailer</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Issuing body</TableCell>
                <TableCell>Cert / License #</TableCell>
                <TableCell>Qualified</TableCell>
                <TableCell>Expiration</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6c757d" }}>Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && !notAvailable && !error && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6c757d" }}>
                    No qualifications match the current filters. Click <strong>New qualification</strong> to add one.
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((q) => {
                const statusColors = STATUS_COLOR[q.status] || { bg: "#e9ecef", color: "#495057" };
                const valid = isQualificationCurrentlyValid(q);
                const detailerLabel = q.user_id
                  ? `User #${q.user_id} (internal)`
                  : `${q.subcontractor_name || "—"} (subcontractor)`;
                return (
                  <TableRow
                    key={q.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/detailer-qualifications/${q.id}`)}
                  >
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailerLabel}</Typography>
                        {q.subcontractor_contact_email && (
                          <Typography variant="caption" sx={{ color: "#6c757d" }}>{q.subcontractor_contact_email}</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{q.qualification_type}</TableCell>
                    <TableCell>{q.issuing_body || "—"}</TableCell>
                    <TableCell>{q.qualification_number || "—"}</TableCell>
                    <TableCell>{q.qualification_date}</TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2">{q.expiration_date || "Non-expiring"}</Typography>
                        <Typography variant="caption" sx={{ color: valid ? "#198754" : "#dc3545" }}>
                          {expirationDescription(q)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={prettyStatus(q.status)}
                        sx={{
                          backgroundColor: statusColors.bg,
                          color: statusColors.color,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
